import {
  ANALYTICS,
  APP_DOMAIN,
  LOGIN_PROVIDERS,
  OPERATOR_LINE,
  PAYMENT_PROVIDER,
  PROCESSOR_GROUPS,
  SERVICE_NAME,
  SITE_DOMAIN,
} from '../operator';
import type { LegalDoc } from '../types';

/*
 * Обязательна по ч. 2 ст. 18.1 152-ФЗ: оператор, собирающий ПДн через сайт,
 * публикует политику на этом сайте. Состав — цели, категории данных и субъектов,
 * правовые основания, действия, сроки, третьи лица, права субъекта.
 */
export const privacy: LegalDoc = {
  id: 'privacy',
  title: 'Политика обработки персональных данных',
  description: `Какие персональные данные обрабатывает ${SERVICE_NAME}, зачем, сколько хранит и кому передаёт.`,
  blocks: [
    { h: '1. Общие положения' },
    {
      p: `1.1. Политика описывает, как обрабатываются персональные данные пользователей сервиса ${SERVICE_NAME} — сайта ${SITE_DOMAIN}, веб-приложения ${APP_DOMAIN}, Telegram-бота и мини-приложения в Telegram (вместе — «Сервис»). Политика составлена по Федеральному закону от 27.07.2006 № 152-ФЗ «О персональных данных».`,
    },
    { p: `1.2. Оператор персональных данных — ${OPERATOR_LINE} (далее — «Оператор»). Связь с Оператором: {contact}.` },
    {
      p: `1.3. Политика действует в отношении всех персональных данных, которые Оператор получает от пользователей Сервиса. Политика опубликована в открытом доступе (ч. 2 ст. 18.1 152-ФЗ) по адресу https://${SITE_DOMAIN}/privacy.`,
    },

    { h: '2. Какие данные, зачем и на каком основании' },
    {
      table: {
        head: ['Цель', 'Данные', 'Основание', 'Срок хранения'],
        rows: [
          [
            'Вход в Сервис и учётная запись',
            `В Telegram — идентификатор, имя и имя пользователя в Telegram. В веб-приложении — идентификатор, имя и адрес электронной почты в ${LOGIN_PROVIDERS}, если пользователь вошёл через них, и ключ сессии в браузере. А также язык интерфейса, часовой пояс, метка источника перехода (с какой страницы или ссылки пришёл пользователь)`,
            'Договор с пользователем (п. 5 ч. 1 ст. 6 152-ФЗ), согласие',
            'Пока учётная запись существует',
          ],
          [
            'Пробное пользование веб-приложением без входа',
            'Учётная запись без имени, которая заводится при первом открытии веб-приложения; ключ сессии в браузере; IP-адрес — чтобы ограничить, сколько таких учётных записей заводится с одного адреса',
            'Договор, согласие',
            'IP-адрес — 1 час. Учётная запись — пока существует; после входа через Яндекс ID или VK ID она становится обычной учётной записью',
          ],
          [
            'Разговорная практика: диалоги с репетитором, исправление ошибок, словарь',
            'Голосовые сообщения, текст реплик, исправления и сохранённые слова; цели и интересы, указанные при знакомстве с Сервисом; настройки',
            'Договор, согласие',
            'Голосовые сообщения не сохраняются: после распознавания хранится только их длительность. Тексты и словарь — пока учётная запись существует',
          ],
          [
            'Персонализация разговора',
            'Короткие заметки о пользователе, которые репетитор выделяет из разговора (например, «готовится к собеседованию»), чтобы возвращаться к ним в следующих беседах',
            'Согласие. Персонализацию можно выключить в настройках — заметки перестанут собираться',
            'Пока учётная запись существует или до отключения персонализации',
          ],
          [
            'Определение уровня языка',
            'Голосовые ответы, их расшифровка, оценки произношения, грамматики и словарного запаса',
            'Договор, согласие',
            'Результаты — пока учётная запись существует; записи голоса не сохраняются',
          ],
          [
            'Статистика и напоминания',
            'Время практики по дням, серия занятий, статистика ошибок',
            'Договор, согласие',
            'Пока учётная запись существует',
          ],
          [
            'Оплата подписки',
            'Сумма, дата и статус платежа, идентификатор платежа и сохранённого способа оплаты у платёжного провайдера; адрес электронной почты или телефон, если пользователь указал их при оплате, — для отправки кассового чека, а почта — ещё и для уведомлений о подписке, если Telegram не подключён или сообщение туда не доставлено. Номер карты Оператор не получает и не хранит',
            'Договор; закон (налоговый учёт)',
            '5 лет (подп. 8 п. 1 ст. 23 Налогового кодекса РФ)',
          ],
          ['Ответы на обращения', 'Аккаунт Telegram, текст обращения', 'Согласие; исполнение договора', '3 года после ответа'],
          [
            'Статистика посещений сайта и оценка рекламы',
            'Файлы cookie, IP-адрес, сведения о браузере и устройстве, просмотренные страницы и действия на них, источник перехода (в том числе рекламная кампания)',
            'Согласие посетителя: продолжая пользоваться сайтом, он соглашается (раздел 7)',
            'У сервиса аналитики — по его правилам (раздел 7)',
          ],
        ],
      },
    },
    { p: '2.1. Субъекты персональных данных — пользователи Сервиса, посетители сайта и лица, обратившиеся к Оператору.' },
    {
      p: '2.2. Оператор не собирает специальные категории персональных данных (о здоровье, убеждениях и др.) и биометрические данные. Голос обрабатывается только чтобы распознать сказанное и оценить произношение, а не чтобы установить личность. Просим не рассказывать в разговорах с репетитором сведения о здоровье, политических взглядах и другие чувствительные данные: репетитор — программа, и такие данные ему не нужны.',
    },
    {
      p: '2.3. Сервис предназначен для лиц старше 18 лет; несовершеннолетние пользуются им только с согласия родителей или законных представителей. Если Оператор узнает, что данные ребёнка младше 14 лет получены без такого согласия, он их удалит.',
    },
    {
      p: `2.4. При входе в веб-приложение через ${LOGIN_PROVIDERS} Оператор получает от этого сервиса идентификатор пользователя, имя и адрес электронной почты — только после того, как пользователь разрешит это в окне входа. Пароль от этих сервисов Оператор не получает.`,
    },

    { h: '3. Что Оператор делает с данными' },
    {
      p: '3.1. Сбор, запись, систематизация, накопление, хранение, уточнение, извлечение, использование, передача (предоставление, доступ), обезличивание, блокирование, удаление и уничтожение — автоматизированно, с использованием баз данных на территории Российской Федерации.',
    },
    {
      p: '3.2. Ответы репетитора, исправления и оценки формируются автоматически, программами на основе нейросетей. Эти оценки служат для обучения и не порождают для пользователя юридических последствий.',
    },

    { h: '4. Кому передаются данные' },
    {
      p: '4.1. Чтобы Сервис работал, Оператор поручает обработку части данных лицам ниже — только в объёме, нужном для этой задачи, и по договору, обязывающему их соблюдать конфиденциальность. Часть из них находится за рубежом, поэтому происходит трансграничная передача — только с согласия пользователя и после уведомления Роскомнадзора (ст. 12 152-ФЗ).',
    },
    {
      table: {
        head: ['Кто', 'Какие данные', 'Зачем'],
        rows: [
          ...PROCESSOR_GROUPS.map((p) => [p.who, p.what, p.why]),
          [PAYMENT_PROVIDER, 'Сумма и назначение платежа, идентификатор пользователя в Сервисе', 'Приём оплаты и выдача кассового чека'],
          [ANALYTICS, 'Данные посетителей сайта из раздела 7', 'Статистика посещений и оценка рекламы'],
        ],
      },
    },
    {
      p: '4.2. Наименования и адреса лиц, которым поручена обработка, Оператор сообщает пользователю по запросу (п. 9 ч. 7 ст. 14 152-ФЗ) — в порядке раздела 6.',
    },
    {
      p: '4.3. Оператор не продаёт персональные данные и не передаёт их для рекламы. Органам власти данные передаются только в случаях, предусмотренных законом.',
    },

    { h: '5. Как данные защищены' },
    {
      p: '5.1. Данные хранятся на сервере в России; доступ к серверу и базе данных есть только у Оператора. Соединения с Сервисом шифруются (HTTPS), доступ к учётной записи подтверждается подписью Telegram, а в веб-приложении — входом через Яндекс ID или VK ID и ключом сессии, который хранится в браузере пользователя. Оператор назначает себя ответственным за организацию обработки персональных данных и принимает иные меры по ст. 18.1 и 19 152-ФЗ.',
    },

    { h: '6. Права пользователя' },
    { p: '6.1. Пользователь вправе:' },
    {
      ul: [
        'узнать, какие его данные обрабатываются, зачем и кому передаются;',
        'потребовать уточнить, заблокировать или удалить данные;',
        'отозвать согласие на обработку персональных данных;',
        'обжаловать действия Оператора в Роскомнадзоре или в суде.',
      ],
    },
    {
      p: '6.2. Запрос направляется Оператору в Telegram: {contact}. Оператор отвечает в течение 10 рабочих дней; срок может быть продлён на 5 рабочих дней с объяснением причин.',
    },
    {
      p: '6.3. После отзыва согласия или запроса на удаление Оператор прекращает обработку и удаляет данные в течение 30 дней, кроме данных, которые закон обязывает хранить дольше (сведения о платежах). Без обработки данных Сервис работать не может, поэтому учётная запись при этом удаляется.',
    },

    { h: '7. Файлы cookie и веб-аналитика' },
    {
      p: `7.1. Cookie — небольшие файлы, которые сайт сохраняет в браузере. Здесь и далее «сайт» — и ${SITE_DOMAIN}, и веб-приложение ${APP_DOMAIN}. Сайт использует cookie и хранилище браузера двух видов:`,
    },
    {
      table: {
        head: ['Вид', 'Зачем'],
        rows: [
          ['Необходимые', 'Работа сайта; в веб-приложении — ключ сессии и прогресс знакомства с Сервисом'],
          [
            'Аналитические и рекламные',
            `Сервис веб-аналитики ${ANALYTICS}: сколько людей заходит на сайт, откуда приходят, какие страницы смотрят и что нажимают, какая реклама приводит пользователей`,
          ],
        ],
      },
    },
    {
      p: '7.2. Cookie устанавливаются, когда посетитель открывает сайт. Продолжая пользоваться сайтом, посетитель соглашается на их использование и на обработку данных из этого раздела.',
    },
    {
      p: '7.3. Сервис веб-аналитики может записывать действия на страницах (прокрутку, нажатия, ввод в поля, кроме паролей и платёжных данных) и получает IP-адрес, сведения о браузере и устройстве. Он обрабатывает эти данные по поручению Оператора и по своей политике конфиденциальности. Оператор видит обезличенную статистику. Чтобы оценивать рекламу, Оператор связывает идентификатор посетителя в сервисе аналитики с учётной записью и сообщает сервису аналитики только факты: пользователь начал пользоваться Сервисом, прошёл знакомство с ним, оплатил подписку (с суммой оплаты). Другие данные учётной записи в сервис аналитики не передаются.',
    },
    {
      p: '7.4. Если посетитель не согласен, ему следует покинуть сайт. Запретить и удалить cookie можно в настройках браузера; сайт при этом продолжит работать.',
    },

    { h: '8. Изменение Политики' },
    {
      p: '8.1. Оператор может менять Политику. Новая редакция действует с момента публикации на этой странице; дата редакции указана в начале документа.',
    },
    {
      p: '8.2. Если меняются цели обработки или состав данных, на которые давалось согласие, Оператор запрашивает согласие заново.',
    },
  ],
};
